import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ParticleBackground } from '@/components/ui/particle-background';
import { authFetch } from '@/api/request';
import { isAuthenticated } from '@/utils/auth';
import logoSrc from '@/assets/logo.png';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [feishuConfigured, setFeishuConfigured] = useState<boolean | null>(null);

  // 已登录直接回首页
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // 探测后端是否配置了飞书登录
  useEffect(() => {
    authFetch('/api/auth/config')
      .then((res) => res.json())
      .then((data) => setFeishuConfigured(!!data.feishuConfigured))
      .catch(() => setFeishuConfigured(false));
  }, []);

  async function handleFeishuLogin() {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch('/api/auth/feishu/url?origin=' + encodeURIComponent(window.location.origin));
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError('获取飞书授权链接失败');
        setLoading(false);
      }
    } catch {
      setError('获取飞书授权链接失败，请检查后端服务');
      setLoading(false);
    }
  }

  // 开发模式：未配置飞书时直接进入（不强制登录）
  if (feishuConfigured === null) {
    return (
      <div className="relative flex h-screen items-center justify-center tech-grid">
        <ParticleBackground count={30} className="opacity-40" />
        <div className="text-sm text-muted-foreground">加载中...</div>
      </div>
    );
  }

  if (!feishuConfigured) {
    return (
      <div className="relative flex h-screen flex-col items-center justify-center gap-4 tech-grid">
        <ParticleBackground count={30} className="opacity-40" />
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-border/60 bg-[hsl(var(--card)/0.6)] px-10 py-8 text-center backdrop-blur-xl">
          <span className="text-3xl">🚀</span>
          <p className="text-sm text-muted-foreground">
            未配置飞书登录（后端缺少 FEISHU_APP_ID）
          </p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="mt-2 rounded-lg bg-[hsl(var(--primary)/0.15)] px-6 py-2 text-xs font-medium text-foreground transition-colors hover:bg-[hsl(var(--primary)/0.25)]"
          >
            开发模式直接进入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-screen items-center justify-center overflow-hidden tech-grid">
      <ParticleBackground count={50} className="opacity-40" />

      {/* Decorative glow */}
      <div className="pointer-events-none absolute -top-32 -right-24 h-96 w-96 rounded-full bg-neon-cyan/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 h-80 w-80 rounded-full bg-neon-purple/10 blur-3xl" />
      {/* LOGO 配色撞色光晕：金黄 + 品红 */}
      <div className="pointer-events-none absolute -top-16 -left-24 h-72 w-72 rounded-full bg-neon-yellow/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -right-20 h-72 w-72 rounded-full bg-neon-pink/10 blur-3xl" />

      <div className="gradient-border relative z-10 flex w-[420px] flex-col items-center rounded-2xl border border-border/60 bg-[hsl(var(--card)/0.7)] p-10 shadow-2xl shadow-black/30 backdrop-blur-xl">
        {/* Logo */}
        <div className="relative mb-4 flex h-14 w-14 items-center justify-center">
          <img src={logoSrc} alt="Playgen Logo" className="h-full w-full object-contain" />
        </div>

        {/* 卡通艺术字 LOGO（透明底横版），替代原文字标题 */}
        <img
          src="/cygf.png"
          alt="创意工坊 · Playgen"
          className="brand-img h-auto w-full max-w-[300px] object-contain"
        />
        <p className="mt-1 text-xs tracking-wider text-muted-foreground">
          试玩广告智能制作平台
        </p>

        <div className="my-6 flex w-full items-center gap-3">
          <div className="h-px flex-1 bg-border/60" />
          <span className="whitespace-nowrap text-[10px] text-muted-foreground/70">
            AI 驱动 · 批量产包 · 飞书协作
          </span>
          <div className="h-px flex-1 bg-border/60" />
        </div>

        {/* Feature highlights — 与后端 GENERATION_MODE_OPTIONS 对齐 */}
        <div className="mb-7 grid w-full grid-cols-2 gap-2.5">
          {[
            { icon: '🧩', label: '内置模板' },
            { icon: '🏗️', label: '蓝图拼装' },
            { icon: '🎮', label: 'Cocos 源码' },
            { icon: '🕹️', label: 'cocos 生成' },
            { icon: '🎬', label: '视频转试玩' },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-center gap-1.5 rounded-xl bg-[hsl(var(--muted)/0.6)] py-2.5 text-xs text-muted-foreground"
            >
              <span className="text-sm">{item.icon}</span>
              {item.label}
            </div>
          ))}
        </div>

        {/* Login button */}
        <button
          onClick={handleFeishuLogin}
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#3370ff] to-[#5b8def] py-3 text-sm font-semibold text-white shadow-lg shadow-[#3370ff]/25 transition-all duration-200 hover:shadow-xl hover:shadow-[#3370ff]/35 disabled:cursor-not-allowed disabled:from-[hsl(var(--muted))] disabled:to-[hsl(var(--muted))] disabled:text-muted-foreground disabled:shadow-none"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              正在跳转...
            </span>
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3.806 7.182a.342.342 0 01.537-.274l7.466 5.228a.342.342 0 010 .56L4.343 17.924a.342.342 0 01-.537-.274V7.182z"
                  fill="currentColor"
                />
                <path
                  d="M12.882 12.418l7.466-5.228a.342.342 0 00-.537-.274l-7.466 5.228a.342.342 0 000 .56l7.466 5.228a.342.342 0 00.537-.274l-7.466-5.228z"
                  fill="currentColor"
                  opacity="0.7"
                />
              </svg>
              飞书扫码登录
            </>
          )}
        </button>

        {/* Error */}
        {error && (
          <div className="mt-4 w-full rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-400">
            {error}
          </div>
        )}

        <p className="mt-8 text-[10px] text-muted-foreground/50">
          Playgen · 面向广告创意团队的试玩智能生产平台
        </p>
      </div>
    </div>
  );
}
